import { Router, watchBusy } from "@frui.ts/screens/dist";
import type { interfaces } from "inversify";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import { Modules } from "config";
import SecurityService from "services/securityService";
import GetExternalReportDetailResponse from "entities/getExternalReportDetailResponse";
import DetailViewModel from "../../../detailViewModel";
import ExternalReportsRepository from "repositories/externalReportsRepository";
import { action, computed, observable } from "mobx";
import * as Sentry from "@sentry/react";
import download from "downloadjs";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";
import { allowToDownload } from "./rights";

@Router.registerRoute({ route: ":reportId", name: Router.Self })
export default class ExternalReportDetailViewModel extends DetailViewModel<GetExternalReportDetailResponse> {
  readonly modelName = "getExternalReportDetailResponse";
  @observable fileResponse: any = undefined;

  constructor(
    protected itemId: string,
    private repository: ExternalReportsRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService
  ) {
    super(itemId);
  }

  @watchBusy
  protected async loadDetail() {
    return this.repository.get(this.itemId);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.timeCharges, `externalReports.${code}`, size);
  }

  @computed
  get rows() {
    return this.item?.downloads.length ?? 0;
  }

  @computed
  get allowToDownload() {
    if (!allowToDownload(this.securityService)) {
      this.notificationService.addNotAllowedError();
      return false;
    }
    return true;
  }

  @action.bound
  @watchBusy
  async onDownload(item?: GetExternalReportDetailResponse) {
    if (item) {
      await this.download(item);
    }
  }

  async download(item: GetExternalReportDetailResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.downloadReport(item.id);
        download(this.fileResponse.data, parseFileNameFromAxiosResponse(this.fileResponse, item.reportName), "application/zip");

        await this.onInitialize();
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) => {
      return new ExternalReportDetailViewModel(
        itemId,
        container.get(ExternalReportsRepository),
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(EventBus),
        container.get(SecurityService)
      );
    };
  }
}
